import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import MicRecorder from "mic-recorder-to-mp3";
import { useSnackbar } from "notistack";
import { Button } from "@mui/material";

const PREFIX = "Mic-Recorder";

const classes = {
  micButtonContainer: `${PREFIX}-micButtonContainer`,
  micButtonContainerDetails: `${PREFIX}-micButtonContainerDetails`,
  micButtonRecordingCenter: `${PREFIX}-micButtonRecordingCenter`,
};

const ConfiguratorIconsContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.micButtonContainer}`]: {
      margin: "0 1rem 0.5rem 1rem",
    },
    [`& .${classes.micButtonContainerDetails}`]: {
      marginBottom: "0.5rem",
      fontSize: "14px",
      marginLeft: "0",
    },
    [`& .${classes.micButtonRecordingCenter}`]: {
      margin: "0 auto",
    },
  };
});

export const useRecordTimer = () => {
  const [timer, setTimer] = useState(0);
  const [start, setStart] = useState(false);
  const firstStart = useRef(true);
  const tick = useRef();

  useEffect(() => {
    if (firstStart.current) {
      firstStart.current = !firstStart.current;
      return;
    }

    if (start) {
      tick.current = setInterval(() => {
        setTimer((timer) => timer + 1);
      }, 1000);
    } else {
      clearInterval(tick.current);
    }

    return () => clearInterval(tick.current);
  }, [start]);

  const toggleStart = () => {
    setStart(true);
  };

  const toggleEnd = () => {
    setStart(false);
    setTimer(0);
  };

  const dispSecondsAsMins = (seconds) => {
    const mins = Math.floor(seconds / 60);
    let seconds_ = seconds % 60;
    let formatedSeconds = seconds_.toString();
    if (formatedSeconds.length === 1) {
      formatedSeconds = "0" + formatedSeconds;
    }
    return (
      mins.toString() + ":" + (formatedSeconds === "0" ? "00" : formatedSeconds)
    );
  };

  return {
    start: toggleStart,
    end: toggleEnd,
    display: dispSecondsAsMins(timer),
  };
};

const recorder = new MicRecorder({ bitRate: 128 });

export const useRecordAudio = (currentObj, saveFncObj, isModal) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isRecording, setIsRecording] = useState(false);
  const myRecordTimer = useRecordTimer();

  useEffect(() => {
    return () => {
      myRecordTimer.end();
    };
  }, []);

  const startRecording = () => {
    const mic = document.getElementById("micButton");
    setIsRecording(true);
    myRecordTimer.start();
    recorder
      .start()
      .then(() => {
        mic.textContent = t("configurator.recordingEnd");
        mic.style.backgroundColor = "#343434";
        mic.style.border = "1px solid #D3AF5F";
        mic.style.color = "#D3AF5F";
      })
      .catch((e) => {
        console.error(e);
        enqueueSnackbar(t("toasts.recordVoiceError"), { variant: "error" });
      });
  };

  const stopRecording = () => {
    const mic = document.getElementById("micButton");
    myRecordTimer.end();
    recorder
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        const newDate = Date.now();
        const file = new File(buffer, `my-record-${newDate}.mp3`, {
          type: blob.type,
          lastModified: newDate,
        });
        const myRecord = URL.createObjectURL(file);
        if (isModal) {
          saveFncObj({ ...currentObj, song: [file], preview: myRecord });
        } else {
          saveFncObj("song", { file: [file], preview: myRecord });
        }
        mic.textContent = t("configurator.recordingStart");
        mic.style.backgroundColor = "#D3AF5F";
        mic.style.border = "";
        mic.style.color = "#343434";
        setIsRecording(false);
      })
      .catch((e) => {
        console.error(e);
        enqueueSnackbar(t("toasts.recordVoiceError"), { variant: "error" });
      });
  };

  return {
    recordButton: (
      <ConfiguratorIconsContainer>
        <Button
          variant="contained"
          color="primary"
          id="micButton"
          size="small"
          className={
            isModal
              ? classes.micButtonContainer
              : !isModal && isRecording
              ? classes.micButtonRecordingCenter
              : classes.micButtonContainerDetails
          }
          onClick={isRecording ? () => stopRecording() : () => startRecording()}
        >
          {t("configurator.recordingStart")}
        </Button>
      </ConfiguratorIconsContainer>
    ),
    isRecording,
    timer: myRecordTimer.display,
  };
};

export const endTileIsDifferentFromStart = (startTile, endTile) => {
  return !(
    startTile.row === endTile.row && startTile.column === endTile.column
  );
};

export const useGetDesc = (item) => {
  const { t } = useTranslation();
  if (!item) return "empty";
  switch (item.type) {
    case "frame":
      if (item.customFrame) return t("configurator.customPainting");
      else
        return item.element?.searchTargets
          ? item.element.searchTargets
          : t("configurator.painting");
    case "ipad":
      return t("myExpariences.form.screenAndSupport");
    case "cup":
      return t("myExpariences.form.cup");
    case "custom":
      return t("myExpariences.form.customModel");
    case "santa1":
      return t("myExpariences.form.santa1");
    case "santa2":
      return t("myExpariences.form.santa2");
    case "basket2":
      return t("myExpariences.form.basket2");
    case "pickleball":
      return t("myExpariences.form.pickleball");
    case "football":
      return t("myExpariences.form.football");
    case "arcade":
      return t("myExpariences.form.arcade");
    case "sled":
      return t("myExpariences.form.sled");
    case "spawn":
      return t("myExpariences.form.spawn");
    case "fazlogo":
      return t("myExpariences.form.fazlogo");
    case "screen":
      return t("myExpariences.form.screen");
    case "designlogo":
      return t("myExpariences.form.designlogo");
    case "christmastree":
      return t("myExpariences.form.christmas-tree");
    case "fw1":
      return t("myExpariences.form.fw1");
    case "fw2":
      return t("myExpariences.form.fw2");
    case "smoke":
      return t("myExpariences.form.smoke");
    case "cyril":
      return "Cyril";
    case "neymar":
      return "Neymar";
    case "alainroche":
      return "Alain Roche";
    case "teqball":
      return t("myExpariences.form.teqball");
    case "arcdetriomphe":
      return "Arc de Triomphe";
    case "logo":
      return t("myExpariences.form.ground-logo");
    case "jaures":
      return t("myExpariences.form.statue");
    case "guest":
      return t("myExpariences.form.guestsSpawn");
    default:
      return t("myExpariences.form.empty");
  }
};

export const typeIsGame = (type) => {
  return ["basket2", "football", "arcade", "pickleball"].includes(type);
};

export const useConfiguratorObjects = (isAdmin, configObjects) => {
  const { t } = useTranslation();
  const [configuratorObjects, setConfiguratorObjects] = useState([]);

  useEffect(() => {
    // isOriented for objects which needs an orientation
    // onFacade for objects which needs to be against a wall
    const fixedObjects =
      configObjects?.map((item) => ({
        ...item,
        value: item.name,
        title: item.title || item.name,
        type: "custom",
      })) || [];
    setConfiguratorObjects([
      ...fixedObjects,
      {
        title: t("myExpariences.form.basket2"),
        value: "basket2",
        isOriented: false,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.pickleball"),
        value: "pickleball",
        isOriented: false,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.football"),
        value: "football",
        isOriented: false,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.arcade"),
        value: "arcade",
        isOriented: false,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.screenAndSupport"),
        value: "ipad",
        isOriented: false,
        onFacade: true,
        isAdmin: !isAdmin,
        disabled: false,
        // disabled: true,
        isEvent: false,
        isTheme: false,
        x: -1,
        y: -1,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.santa1"),
        value: "santa1",
        isOriented: true,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.santa2"),
        value: "santa2",
        isOriented: true,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.sled"),
        value: "sled",
        isOriented: false,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 1,
        y: 1,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.spawn"),
        value: "spawn",
        isOriented: true,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: "Cyril",
        value: "cyril",
        isOriented: true,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: "Neymar",
        value: "neymar",
        isOriented: true,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: "Alain Roche",
        value: "alainroche",
        isOriented: true,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.christmas-tree"),
        value: "christmastree",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.fw1"),
        value: "fw1",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: true,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.fw2"),
        value: "fw2",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: true,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.smoke"),
        value: "smoke",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: true,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: "FanArtZone logo",
        value: "fazlogo",
        isOriented: false,
        onFacade: true,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 1,
        y: 1,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: "Design logo",
        value: "designlogo",
        isOriented: false,
        onFacade: true,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 1,
        y: 1,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: "Arc de Triomphe",
        value: "arcdetriomphe",
        isOriented: false,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 2,
        y: 3,
        associateCoords: true,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.screen"),
        value: "screen",
        isOriented: false,
        onFacade: true,
        isAdmin: !isAdmin,
        disabled: false,
        // disabled: true,
        isEvent: false,
        isTheme: false,
        x: -1,
        y: -1,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.teqball"),
        value: "teqball",
        isOriented: false,
        onFacade: false,
        isAdmin: !isAdmin,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 1,
        y: 1,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.statue"),
        value: "jaures",
        isOriented: true,
        onFacade: false,
        disabled: false,
        isEvent: false,
        isTheme: false,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: true,
      },
      {
        title: t("myExpariences.form.song"),
        value: "song",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: false,
        isTheme: true,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.SongAndFilter"),
        value: "songAndFilter",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: false,
        isTheme: true,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.blackAndWhiteFilter"),
        value: "blackAndWhite",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: false,
        isTheme: true,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.sepiaFilter"),
        value: "sepia",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: false,
        isTheme: true,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: false,
      },
      {
        title: t("myExpariences.form.rainyFilter"),
        value: "rainy",
        isOriented: false,
        onFacade: false,
        disabled: false,
        isEvent: false,
        isTheme: true,
        x: 0,
        y: 0,
        associateCoords: false,
        unlimited: false,
      },
    ]);
  }, [isAdmin, configObjects]);

  return configuratorObjects;
};

export const countMyDesigns = (elements) => {
  let tmp = { counter: 0, directions: [] };
  for (const elem in elements) {
    if (elements[elem].type === "painting" || elements[elem].type === "frame") {
      tmp.directions.push(elem);
      tmp.counter += 1;
    }
  }
  return tmp;
};

export function validURL(str) {
  var pattern = new RegExp(
    "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
    "i"
  ); // fragment locator
  return !!pattern.test(str);
}

export const allVideosContentValid = (links) => {
  let valid = true;
  links?.forEach((item) => {
    if (!item.hasOwnProperty("link") || (item.link && item.link.length === 0))
      valid = false;
  });
  return valid;
};

export const gamesTypes = ["basket", "football", "arcade", "pickleball"];
export const gamesKeys = ["basket2", "football", "arcade", "pickleball"];
export const sortOrder = ["basket2", "football", "arcade", "pickleball"];
export const gamesSizesHelper = {
  basket: { row: 25, column: 17, exist: false },
  football: { row: 25, column: 17, exist: false },
  arcade: { row: 6, column: 3, exist: false },
  pickleball: { row: 19, column: 13, exist: false },
};
